import type { RosterRowRef } from '@discovr/features';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import { isMain } from '@discovr/types';
import { ActionConfirmDialog, CONFIRM_WORD } from '../../../components/common/ActionConfirmDialog';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { resolveCreatorPlatformProfile } from '../../../lib/platformProfile';
import { DeliverablePicker } from './DeliverablePicker';
import type { CreatorsRow } from './types';

/** Confirm-button label for the creator hard-delete. */
const DELETE_CONFIRM_LABEL = 'Delete creator';

export interface ConfirmAction {
  variant: 'approve-as-brand' | 'brand-approve' | 'admin-approve' | 'reject' | 'delete' | 'reinstate';
  creator: CreatorsRow;
}

const APPROVE_VARIANTS: ReadonlySet<ConfirmAction['variant']> = new Set([
  'approve-as-brand',
  'brand-approve',
  'admin-approve',
]);

interface ApprovalCardProps {
  confirmAction: ConfirmAction;
  confirmBusy: boolean;
  /** Full selection: main type + any add-ons. Empty means nothing pre-selected. */
  approveAsBrandDeliverables: string[];
  campaignPlatform: string | undefined;
  /**
   * True when the viewer is acting as an admin. Varies the reject-dialog copy
   * (admin reject → brand sees the reason; brand reject → it feeds future
   * creator recommendations).
   */
  isAdminViewer: boolean;
  onClose: () => void;
  onConfirm: (reason?: string, typedConfirm?: { confirm: string }) => Promise<void>;
  /** Replace the current selection with a new full array (main + add-ons). */
  onSetApproveAsBrandDeliverables: (types: string[]) => void;
  approveCreatorAsBrand?: ((creator: RosterRowRef, types: string[]) => Promise<void>) | null;
  rejectCreatorApproval?: ((creator: RosterRowRef, reason: string) => Promise<void>) | null;
}

export function ApprovalCard({
  confirmAction,
  confirmBusy,
  approveAsBrandDeliverables,
  campaignPlatform,
  isAdminViewer,
  onClose,
  onConfirm,
  onSetApproveAsBrandDeliverables,
}: ApprovalCardProps) {
  const platform = campaignPlatform ?? '';
  const variant = confirmAction.variant;
  const creator = confirmAction.creator;

  // Read-only members may see deliverables + prices but cannot change the
  // selection or confirm. Disables the pickers, no-ops the toggles, and
  // suppresses the confirm action below.
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const isReadOnly = accessLevel === 'read';
  const creatorName = rowDisplayName(creator);
  // Show only the @username, clickable through to the profile — same pattern
  // as PlatformCreatorCell across the roster tabs.
  const { username, profileUrl, isVerified } = resolveCreatorPlatformProfile(
    creator as unknown as Record<string, unknown>,
    campaignPlatform,
  );

  // Fixed-word gate only for the (irreversible) delete; reject/approve have none.
  const deleteConfirmWord = variant === 'delete' ? CONFIRM_WORD : undefined;
  // ActionConfirmDialog's DEFAULT_LABELS maps 'delete' to the generic "Delete"
  // shared with object deletes (products, assignments); name the creator here.
  const deleteConfirmLabel = variant === 'delete' ? DELETE_CONFIRM_LABEL : undefined;

  const copy = (() => {
    switch (variant) {
      case 'approve-as-brand':
        return {
          title: 'Approve on behalf of brand?',
          description:
            "You're recording a brand approval for this creator. Pick the deliverable(s) the brand has agreed to — your selection is sent only when you confirm.",
        };
      case 'brand-approve':
        return {
          title: 'Approve Creator',
          description: 'Select the primary deliverable for this creator. This cannot be changed after approval.',
        };
      case 'admin-approve':
        return {
          title: 'Confirm admin approval?',
          description:
            "Review the deliverable(s) selected by the brand and confirm to finalize this creator.",
        };
      case 'reject':
        // Admin copy stays distinct: the reason is shown to the brand, not fed
        // into future-recommendation tuning.
        return {
          title: 'Reject creator',
          description: isAdminViewer
            ? 'This rejects the creator. Your reason, if provided, will be visible to the brand.'
            : 'Share your reasoning to help us improve your future creator recommendations.',
        };
      case 'delete':
        return {
          title: 'Delete creator permanently?',
          description:
            'This erases the creator from this campaign along with everything attached to them. They will NOT appear on the Rejected tab and cannot be sent back to Pending — re-import them to start over.',
        };
      case 'reinstate':
        return {
          title: 'Send back to Pending?',
          description:
            'This creator returns to the Pending tab for the brand to review again. Their prices and stats are kept; deliverables can be re-picked from there.',
        };
    }
  })();

  const showsDeliverablePicker = APPROVE_VARIANTS.has(variant);
  const readOnlyFootnote = isReadOnly
    ? 'View-only — you do not have access to change or confirm this selection.'
    : 'Selection is sent only on confirm. Cancel to discard.';
  const extraContent = showsDeliverablePicker ? (
    <div className="space-y-3">
      <DeliverablePicker
        deliverables={creator.deliverables}
        platform={platform}
        selected={approveAsBrandDeliverables}
        onChange={onSetApproveAsBrandDeliverables}
        disabled={isReadOnly}
      />
      {creator.deliverables.length > 0 && (
        <p className="text-[10px] font-medium text-muted-foreground">{readOnlyFootnote}</p>
      )}
    </div>
  ) : undefined;

  // At least one main deliverable must be selected to confirm. Read-only
  // members can never confirm — the deliverable picker is view-only.
  const canConfirm = !isReadOnly && (!showsDeliverablePicker || approveAsBrandDeliverables.some((t) => isMain(t, platform)));

  return (
    <ActionConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant={variant}
      title={copy.title}
      description={copy.description}
      creator={{
        name: creatorName,
        handle: username ? `@${username}` : undefined,
        profileUrl: profileUrl || undefined,
        isVerified,
      }}
      onConfirm={onConfirm}
      busy={confirmBusy}
      extraContent={extraContent}
      canConfirm={canConfirm}
      confirmText={deleteConfirmWord}
      confirmLabel={deleteConfirmLabel}
      {...(variant === 'reject' && !isAdminViewer
        ? {
            // Brand rejection requires a reason; admin's reject keeps it optional.
            reasonLabel: 'Reason for rejection',
            reasonMinLength: 1,
            reasonPlaceholder: "e.g., Budget mismatch, content style doesn't fit...",
          }
        : {})}
    />
  );
}
